import type { BrandTokenFamilyKind, BrandTokenSetViewDto, BrandTokenViewDto } from '@/types/knowledgeBase'
import { CopyButton } from '@/components/common/CopyButton'
import { TextBox } from '@/components/common/TextBox'
import { ownTokens, pxOf, storedTokenValue, tokenNoun, type TokenEditor } from '@/components/console/knowledgeBase/brandGuidelinesModel'
import { AddButton, InlineForm, Refusal, RemoveQuestion, RolePills, RowActions } from './BrandEditControls'
import { H2, LEDE, MONO, SWATCH_FRAME } from './brandGuidelinesStyles'
import { roleNote, type Pointers, type TokenEditing } from './brandEditing'

type ScaleKind = Exclude<BrandTokenFamilyKind, 'Color'>

/**
 * How each scale is titled, counted and described when empty. Spacing shows no usage anywhere, so its form has no field
 * for one, and a change sends back the usage the step has.
 */
const SCALES: Record<ScaleKind, {
  title: string
  unit: readonly [one: string, many: string]
  empty: string
  add: string
  name: string
  value: string
  usage: string | null
}> = {
  Spacing: {
    title: 'Spacing', unit: ['step', 'steps'], empty: 'add steps here, such as 4, 8, 16 and 24 px.', add: 'Add a step',
    name: 'space-lg', value: '24px', usage: null,
  },
  Radius: {
    title: 'Radius', unit: ['value', 'values'], empty: 'add the corner roundness for buttons and cards here.', add: 'Add a radius',
    name: 'radius-md', value: '8px', usage: 'What it is for, such as Buttons and cards.',
  },
  Shadow: {
    title: 'Shadow', unit: ['value', 'values'], empty: 'add shadows for raised cards and menus here.', add: 'Add a shadow',
    name: 'shadow-md', value: '0 2px 8px rgba(0,0,0,.12)', usage: 'What it is for, such as Menus.',
  },
}

const KINDS: readonly ScaleKind[] = ['Spacing', 'Radius', 'Shadow']

interface Props {
  set: BrandTokenSetViewDto | null
  pointers: Pointers
  canChange: boolean
  pending: boolean
  edit: TokenEditing
  locked: boolean
}

/** Spacing, radius and shadow side by side, each always shown and, on the company's own page, changed in place (ORB-973). */
export function BrandScaleSections(props: Props) {
  return (
    <div className="px-6 sm:px-8 py-12 sm:py-14 grid grid-cols-1 xl:grid-cols-3 gap-14 xl:gap-10">
      {KINDS.map(kind => <Scale key={kind} kind={kind} {...props} />)}
    </div>
  )
}

function Scale({ kind, set, pointers, canChange, pending, edit, locked }: Props & { kind: ScaleKind }) {
  const scale = SCALES[kind]
  const tokens = ownTokens(set?.[kind] ?? [])
  const themeId = set?.DefaultThemeId ?? null
  const ground = set?.Roles.Background.Hex ?? '#F8FAFC'
  const headingId = `bg-${kind.toLowerCase()}-h`
  const editor = edit.editor?.kind === kind ? edit.editor : null
  const fields = scale.usage === null ? ['Name', 'Value'] : ['Name', 'Value', 'Usage']

  // A token with a value per theme is changed in the theme emails and pages use, as a color is.
  const change = (token: BrandTokenViewDto) => edit.open(kind, token.Name, null, {
    Name: token.Name, Value: storedTokenValue(token, themeId), Usage: token.Usage ?? '',
  })
  const form = () => editor && (
    <TokenForm kind={kind} editor={editor} pending={pending}
      note={editor.current === null ? null : roleNote(pointers.get(editor.current), tokenNoun(kind))}
      onField={edit.setField} onCancel={edit.close} onSave={() => void edit.save(fields)} />
  )

  const [one, many] = scale.unit
  const lede = tokens.length > 0
    ? `${tokens.length} ${tokens.length === 1 ? one : many}.${
      canChange ? ` ${many.charAt(0).toUpperCase()}${many.slice(1)} you add, change or remove here stay that way when Populate from website runs.` : ''}`
    : canChange ? `None yet. Populate from website fills this, or ${scale.empty}` : 'None yet.'
  const List = kind === 'Spacing' ? 'ol' : 'ul'

  return (
    <section aria-labelledby={headingId} className="min-w-0 flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <h2 id={headingId} className={H2}>{scale.title}</h2>
        <p className={LEDE}>{lede}</p>
      </div>
      {tokens.length > 0 && (
        <List aria-labelledby={headingId} className={`flex flex-col ${kind === 'Spacing' ? 'gap-1' : 'gap-4'}`}>
          {tokens.map(token => {
            if (editor?.current === token.Name) return <li key={token.Name}>{form()}</li>
            const ref = { kind, name: token.Name, slot: null }
            const refusal = edit.refusalOf(kind, token.Name)
            return (
              <li key={token.Name} className="min-w-0 flex flex-col gap-1.5">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    {kind === 'Spacing' ? <Step token={token} /> : <Sample kind={kind} token={token} ground={ground} pointers={pointers} />}
                  </div>
                  {canChange && (
                    <span className="flex-none">
                      <RowActions iconOnly kind={kind} name={token.Name} pending={pending || locked}
                        onChange={() => change(token)} onRemove={() => edit.ask(ref)} />
                    </span>
                  )}
                </div>
                {edit.isAsking(kind, token.Name) && (
                  <RemoveQuestion kind={kind} name={token.Name} pending={pending} onRemove={() => void edit.remove(ref)} onKeep={edit.keep} />
                )}
                {refusal && <Refusal message={refusal} />}
              </li>
            )
          })}
        </List>
      )}
      {canChange && (editor !== null && editor.current === null
        ? form()
        : <AddButton label={scale.add} kind={kind} slot={null} pending={pending || locked}
          onClick={() => edit.open(kind, null, null, { Name: '', Value: '', Usage: '' })} />)}
    </section>
  )
}

/** A spacing step with a bar its own length, so the steps compare as they will on a page. */
function Step({ token }: { token: BrandTokenViewDto }) {
  const px = pxOf(token.ResolvedValue)
  return (
    <div className="grid grid-cols-[88px_56px_minmax(0,1fr)] items-center gap-3 min-h-8">
      <span className={`${MONO} text-slate-900 dark:text-white truncate`}>{token.Name}</span>
      <span className={`${MONO} text-slate-600 dark:text-slate-300 truncate`}>{token.ResolvedValue}</span>
      {px !== null && px > 0
        ? <span aria-hidden="true" className="block h-2 max-w-full rounded-sm bg-slate-400 dark:bg-slate-500" style={{ width: `${px}px` }} />
        : <span />}
    </div>
  )
}

/** A radius or shadow drawn as it will look: a corner, or a card raised off the brand's own page background. */
function Sample({ kind, token, ground, pointers }: {
  kind: 'Radius' | 'Shadow'
  token: BrandTokenViewDto
  ground: string
  pointers: Pointers
}) {
  const value = token.ResolvedValue ?? null
  return (
    <div className="flex items-center gap-3 min-w-0">
      {kind === 'Radius' ? (
        <span aria-hidden="true" className="w-11 h-8 flex-none bg-slate-50 dark:bg-slate-900 ring-[1.5px] ring-inset ring-slate-500 dark:ring-slate-400"
          style={{ borderRadius: value ?? undefined }} />
      ) : (
        <span aria-hidden="true" className={`w-[88px] h-16 flex-none grid place-items-center ${SWATCH_FRAME}`} style={{ background: ground }}>
          <span className="block w-[52px] h-[30px] rounded-md" style={{ background: '#FFFFFF', boxShadow: value ?? undefined }} />
        </span>
      )}
      <div className="min-w-0 flex flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`${MONO} font-medium text-slate-900 dark:text-white`}>{token.Name}</span>
          <RolePills name={token.Name} pointers={pointers} />
        </div>
        {value && (kind === 'Shadow' ? (
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="min-w-0 font-mono text-xs text-slate-600 dark:text-slate-300 break-words">{value}</span>
            <CopyButton text={value} label={token.Name} />
          </span>
        ) : <span className={`${MONO} text-slate-600 dark:text-slate-300`}>{value}</span>)}
        {token.Usage && <span className="text-sm text-slate-600 dark:text-slate-300">{token.Usage}</span>}
      </div>
    </div>
  )
}

function TokenForm({ kind, editor, pending, note, onField, onCancel, onSave }: {
  kind: ScaleKind
  editor: TokenEditor
  pending: boolean
  note: string | null
  onField: (key: string, value: string) => void
  onCancel: () => void
  onSave: () => void
}) {
  const scale = SCALES[kind]
  const id = `bg-${kind.toLowerCase()}`
  const was = editor.current === null ? null : editor.was.Value
  // A shadow's value runs long, so it takes the form's full width.
  const span = kind === 'Shadow' ? 'col-span-2' : undefined
  return (
    <InlineForm editor={editor} pending={pending} note={note} onCancel={onCancel} onSave={onSave}>
      <div className="grid grid-cols-2 gap-3">
        <TextBox name={`${id}-name`} label="Name" density="compact" value={editor.fields.Name} disabled={pending} containerClassName={span}
          placeholder={scale.name} error={editor.errors.Name} onChange={value => onField('Name', value)} />
        <TextBox name={`${id}-value`} label="Value" density="compact" value={editor.fields.Value} disabled={pending} containerClassName={span}
          placeholder={scale.value} error={editor.errors.Value}
          helperText={was !== null && was !== editor.fields.Value ? `Was ${was}` : undefined}
          onChange={value => onField('Value', value)} />
        {scale.usage !== null && (
          <TextBox name={`${id}-usage`} label="Usage" density="compact" value={editor.fields.Usage} disabled={pending} containerClassName="col-span-2"
            placeholder={scale.usage} error={editor.errors.Usage} onChange={value => onField('Usage', value)} />
        )}
      </div>
    </InlineForm>
  )
}
